import { Env } from '@core/env.ts'
import { type Logger } from '@core/logging/logger.ts'
import { Timer } from '@core/timer/timer.ts'

/**
 * A source of "the location may have changed" signals. Two implementations exist:
 * an event-driven one backed by the Navigation API (preferred) and a polling
 * fallback for browsers that lack it. Both invoke the same {@link NavigationListener};
 * the consumer ({@link PageViewProcessor}) decides whether the change is a real page view.
 *
 * @internal
 */
export interface NavigationObserver {
    _start(): void
    _stop(): void
}

/** Invoked when a navigation (or, in the fallback, a poll tick) occurs. @internal */
export type NavigationListener = () => void | Promise<void>

/** Legacy fallback poll cadence — matches the historical 1-second page-view timer. */
const PAGE_VIEW_POLL_INTERVAL_SECONDS = 1

/**
 * Returns the {@link NavigationApiObserver} when the Navigation API is available
 * (Baseline as of 2026 — recent Chrome/Edge/Firefox/Safari), otherwise the
 * {@link PollingNavigationObserver} fallback.
 *
 * @internal
 */
export function createNavigationObserver(listener: NavigationListener, logger: Logger): NavigationObserver {
    if (NavigationApiObserver._isSupported) {
        logger.debug('Using Navigation API for page view detection')
        return new NavigationApiObserver(listener)
    }

    logger.debug('Navigation API unavailable — falling back to polling for page view detection')
    return new PollingNavigationObserver(listener)
}

/**
 * Observes navigations via the Navigation API. `currententrychange` fires after a
 * navigation commits — covering SPA `pushState`/`replaceState`, hash changes, and
 * back/forward — so we observe rather than intercept (Safari's missing
 * `precommitHandler` is therefore irrelevant here).
 *
 * @internal
 */
export class NavigationApiObserver implements NavigationObserver {
    public static get _isSupported(): boolean {
        const nav = (Env._scope as any).navigation
        return !!nav && typeof nav.addEventListener === 'function'
    }

    private readonly handler = (): void => {
        void this.listener()
    }

    public constructor(private readonly listener: NavigationListener) {}

    private get _navigation(): EventTarget {
        return (Env._scope as any).navigation
    }

    public _start(): void {
        this._navigation.addEventListener('currententrychange', this.handler)
    }

    public _stop(): void {
        this._navigation.removeEventListener('currententrychange', this.handler)
    }
}

/**
 * Legacy fallback. Polls on a fixed interval and lets the consumer's in-memory
 * last-URL comparison debounce repeat ticks on the same page.
 *
 * @internal
 */
export class PollingNavigationObserver implements NavigationObserver {
    private timer: Timer | null = null

    public constructor(private readonly listener: NavigationListener) {}

    public _start(): void {
        if (this.timer) {
            return
        }

        // runImmediatelyOnStart=false: PageViewProcessor._start() already runs an initial
        // evaluation, so the poll should not double-fire the first tick.
        this.timer = new Timer('pvp_timer', () => void this.listener(), PAGE_VIEW_POLL_INTERVAL_SECONDS, {
            autoStart: false,
            runImmediatelyOnStart: false,
        })
        this.timer._start()
    }

    public _stop(): void {
        this.timer?._stop()
        this.timer = null
    }
}
